// Master Order API client — thin wrappers over the shared axios `api` instance.
// Field names mirror /app/backend/master_order/schemas.py exactly (client/order/order-item/product-type/settings/access-mode).
import { api } from "@/lib/api";

const p = (path) => `/master-order${path}`;

/* ---------------------------------- access -------------------------------- */
export const getAccessMode = async ({ demo } = {}) => {
  const { data } = await api.get(p("/access-mode"), { params: demo ? { demo: true } : {} });
  return data; // { mode: internal|full|read_only|demo|no_access }
};

/* --------------------------------- settings ------------------------------- */
export const getSettings = async () => {
  const { data } = await api.get(p("/settings"));
  return data; // null OR MasterOrderSettings
};

/* ------------------------------- product-types ---------------------------- */
export const listProductTypes = async () => (await api.get(p("/product-types"))).data;
export const createProductType = async (payload) => (await api.post(p("/product-types"), payload)).data;

/* --------------------------------- clients -------------------------------- */
export const listClients   = async () => (await api.get(p("/clients"))).data;
export const getClient     = async (id) => (await api.get(p(`/clients/${id}`))).data;
export const createClient  = async (payload) => (await api.post(p("/clients"), payload)).data;
export const updateClient  = async (id, payload) => (await api.patch(p(`/clients/${id}`), payload)).data;
export const listClientOrders = async (id) => (await api.get(p(`/clients/${id}/orders`))).data;

/* ---------------------------------- orders -------------------------------- */
export const listOrders    = async (params = {}) => (await api.get(p("/orders"), { params })).data;
export const getOrder      = async (id) => (await api.get(p(`/orders/${id}`))).data;
export const createOrder   = async (payload) => (await api.post(p("/orders"), payload)).data;
export const updateOrder   = async (id, payload) => (await api.patch(p(`/orders/${id}`), payload)).data;
export const listOrderReminders = async (orderId) =>
  (await api.get(p(`/orders/${orderId}/reminders`))).data;
export const createOrderReminder = async (orderId, payload) =>
  (await api.post(p(`/orders/${orderId}/reminders`), payload)).data;
export const updateOrderReminder = async (id, payload) =>
  (await api.patch(p(`/reminders/${id}`), payload)).data;
export const deleteOrderReminder = async (id) =>
  api.delete(p(`/reminders/${id}`));
export const listAttentionReminders = async (onDate) =>
  (await api.get(p("/reminders/attention"), { params: { on_date: onDate } })).data;

// ── Product Cost Snapshots (Sprint Product-Cost-Attach) ────────────
// Attach the financial layer of a Workbook product_cost_calculation to
// an Order. Duplicates raise 409 with the existing snapshot payload so
// the UI can prompt for update instead of duplicating silently.
export const listOrderProductCostSnapshots = async (orderId) =>
  (await api.get(p(`/orders/${orderId}/product-cost-snapshots`))).data;
export const createProductCostSnapshot = async (payload) =>
  (await api.post(p("/product-cost-snapshots"), payload)).data;
export const updateProductCostSnapshot = async (id, payload) =>
  (await api.patch(p(`/product-cost-snapshots/${id}`), payload)).data;
export const deleteProductCostSnapshot = async (id) =>
  (await api.delete(p(`/product-cost-snapshots/${id}`))).data;

/** Strip legacy 4-digit zfill from an order_number for user-facing display. */
export const displayOrderNumber = (n) => {
  if (n == null) return "—";
  const s = String(n).replace(/^0+/, "");
  return s || "0";
};

/* -------------------------------- order-items ----------------------------- */
export const listOrderItems  = async (orderId) => (await api.get(p(`/orders/${orderId}/items`))).data;
export const createOrderItem = async (payload) => (await api.post(p("/order-items"), payload)).data;
export const updateOrderItem = async (id, payload) => (await api.patch(p(`/order-items/${id}`), payload)).data;
export const deleteOrderItem = async (id) => (await api.delete(p(`/order-items/${id}`))).data;
export const listOrderItemClientReferences = async (id) =>
  (await api.get(p(`/order-items/${id}/client-references`))).data;

/* ---------------- Finished Order block (Step 2) --------------------------
 * Photo previews cannot be built from Media ids alone (no user-facing
 * per-Media GET). This resolver returns [{id, file_url}] for the order's
 * saved finished_photo_media_ids, applying the same ownership filter used
 * at attach-time.
 * ----------------------------------------------------------------------- */
export const listFinishedPhotos = async (orderId) =>
  (await api.get(p(`/orders/${orderId}/finished-photos`))).data;

// Reuses the existing platform upload endpoint — no new media bucket.
// Returns the full Media record ({id, file_url, ...}).
export const uploadUserMediaFile = async (file, { title } = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  if (title) fd.append("title", title);
  const { data } = await api.post("/account/media/upload-file", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

/* ---------------- Payment ledger (Sprint Payment-2 — read-only) ----------
 * Backend is the source of truth for every aggregate. Frontend only formats.
 * ------------------------------------------------------------------------ */
export const getOrderPaymentsSummary = async (orderId) =>
  (await api.get(p(`/orders/${orderId}/payments/summary`))).data;

/* ---------------- Payment ledger (Sprint Payment-3 — manual journal) ------ */
export const listOrderPayments   = async (orderId) =>
  (await api.get(p(`/orders/${orderId}/payments`))).data;
export const createOrderPayment  = async (payload) =>
  (await api.post(p("/payments"), payload)).data;
export const updateOrderPayment  = async (paymentId, payload) =>
  (await api.patch(p(`/payments/${paymentId}`), payload)).data;
export const voidOrderPayment    = async (paymentId, payload = {}) =>
  (await api.post(p(`/payments/${paymentId}/void`), payload)).data;

/* ---------------------------------- enums --------------------------------- */
export const listIngredients   = async () => (await api.get(p("/ingredients"))).data;
export const createIngredient  = async (payload) => (await api.post(p("/ingredients"), payload)).data;
export const updateIngredient  = async (id, payload) => (await api.patch(p(`/ingredients/${id}`), payload)).data;
export const listIngredientPricesByIng = async (id) => (await api.get(p(`/ingredient-prices/by-ingredient/${id}`))).data;
export const createIngredientPrice = async (payload) => (await api.post(p("/ingredient-prices"), payload)).data;

// Machine keys stay aligned with backend enums. All Master Order screens use
// this single display layer so API/DB values never leak into Russian UI.
export const UNIT_LABELS = Object.freeze({
  g: "г",
  kg: "кг",
  ml: "мл",
  l: "л",
  pcs: "шт.",
  tsp: "ч. л.",
  tbsp: "ст. л.",
  cup: "чашка",
  pinch: "щепотка",
  handful: "горсть",
  taste: "по вкусу",
  cm: "см",
  m: "м",
});
export const unitLabel = (unit) => UNIT_LABELS[unit] || unit || "—";

// Backend enums — package2_schemas.py
export const INGREDIENT_BASE_UNITS = [
  { value: "g",   label: `${unitLabel("g")} (масса)`,   family: "mass"   },
  { value: "ml",  label: `${unitLabel("ml")} (объём)`,  family: "volume" },
  { value: "pcs", label: `${unitLabel("pcs")} (штуки)`, family: "count"  },
];
// PurchaseUnit ⊃ base units + kg + l. Frontend pre-filters by family, backend
// still validates dimensional compatibility strictly.
export const PURCHASE_UNITS_BY_FAMILY = {
  mass:   [ { value: "g",   label: unitLabel("g")   }, { value: "kg", label: unitLabel("kg") } ],
  volume: [ { value: "ml",  label: unitLabel("ml")  }, { value: "l",  label: unitLabel("l")  } ],
  count:  [ { value: "pcs", label: unitLabel("pcs") } ],
};
export const unitFamily = (baseUnit) =>
  INGREDIENT_BASE_UNITS.find((u) => u.value === baseUnit)?.family || "mass";

export const listRecipes       = async () => (await api.get(p("/recipes"))).data;
export const getRecipe         = async (id) => (await api.get(p(`/recipes/${id}`))).data;
export const createRecipe      = async (payload) => (await api.post(p("/recipes"), payload)).data;
export const updateRecipe      = async (id, payload) => (await api.patch(p(`/recipes/${id}`), payload)).data;

// Recipe yield unit ⊃ PurchaseUnit — same enum as ingredient purchase unit.
// Frontend narrows options per family to avoid dimensionally-impossible yields.
export const RECIPE_YIELD_FAMILIES = [
  { value: "mass",   label: "Масса",  units: [ { value: "g",  label: unitLabel("g")  }, { value: "kg", label: unitLabel("kg") } ] },
  { value: "volume", label: "Объём",  units: [ { value: "ml", label: unitLabel("ml") }, { value: "l",  label: unitLabel("l")  } ] },
  { value: "count",  label: "Штуки",  units: [ { value: "pcs", label: unitLabel("pcs") } ] },
];
export const yieldUnitFamily = (unit) =>
  RECIPE_YIELD_FAMILIES.find((f) => f.units.some((u) => u.value === unit))?.value || "mass";

// Compatible scaling methods per yield family (matches backend Costing rules).
export const scalingMethodsForFamily = (family) => {
  if (family === "count")  return ["quantity", "manual"];
  if (family === "mass")   return ["weight", "manual"];
  if (family === "volume") return ["manual"]; // no direct dimension in Costing
  return ["manual"];
};
export const SCALING_METHOD_LABELS = {
  quantity: "По количеству",
  weight:   "По массе",
  manual:   "Ручной коэффициент",
  geometry: "По геометрии (в разработке)",
};

export const listPackagingItems = async () => (await api.get(p("/packaging-items"))).data;
export const createPackagingItem = async (payload) => (await api.post(p("/packaging-items"), payload)).data;
export const updatePackagingItem = async (id, payload) => (await api.patch(p(`/packaging-items/${id}`), payload)).data;
export const listPackagingPricesByItem = async (id) => (await api.get(p(`/packaging-prices/by-item/${id}`))).data;
export const createPackagingPrice = async (payload) => (await api.post(p("/packaging-prices"), payload)).data;

// Backend enums — package2_schemas.py::PackagingBaseUnit / PackagingPurchaseUnit.
// Обратите внимание: у упаковки НЕТ kg и l — только g/ml. Плюс появляются cm и m (LENGTH-семья).
export const PACKAGING_BASE_UNITS = [
  { value: "pcs", label: unitLabel("pcs"), family: "count"  },
  { value: "cm",  label: unitLabel("cm"),  family: "length" },
  { value: "g",   label: unitLabel("g"),   family: "mass"   },
  { value: "ml",  label: unitLabel("ml"),  family: "volume" },
];
export const PACKAGING_PURCHASE_UNITS_BY_FAMILY = {
  count:  [ { value: "pcs", label: unitLabel("pcs") } ],
  length: [ { value: "cm",  label: unitLabel("cm") }, { value: "m", label: unitLabel("m") } ],
  mass:   [ { value: "g",   label: unitLabel("g")  } ],
  volume: [ { value: "ml",  label: unitLabel("ml") } ],
};
export const packagingUnitFamily = (baseUnit) =>
  PACKAGING_BASE_UNITS.find((u) => u.value === baseUnit)?.family || "count";
export const packagingUnitLabel = unitLabel;

export const createCostingDraft = async (payload) => (await api.post(p("/costings/draft"), payload)).data;
export const getCostingCurrent  = async (orderItemId) => (await api.get(p(`/costings/order-item/${orderItemId}/current`))).data;
export const listCostingVersions= async (orderItemId) => (await api.get(p(`/costings/order-item/${orderItemId}/versions`))).data;
export const getCosting         = async (id) => (await api.get(p(`/costings/${id}`))).data;
export const updateCosting      = async (id, payload) => (await api.patch(p(`/costings/${id}`), payload)).data;
export const recalcCosting      = async (id) => (await api.post(p(`/costings/${id}/recalculate`))).data;
export const finalizeCosting    = async (id) => (await api.post(p(`/costings/${id}/finalize`))).data;
export const nextDraftCosting   = async (id) => (await api.post(p(`/costings/${id}/next-draft`))).data;
export const orderCostingSummary= async (orderId) => (await api.get(p(`/costings/order/${orderId}/summary`))).data;

// Backend error → Russian UI message. Keys are the backend enum string values (upper-cased).
// Source of truth: /app/backend/master_order/package2_schemas.py::CostingErrorCode.
export const COSTING_ERROR_MSG = {
  INCOMPATIBLE_DIMENSIONAL_UNITS:              "Единицы измерения несовместимы",
  INVALID_UNIT_CONVERSION:                     "Некорректное преобразование единиц",
  INVALID_MANUAL_PRICE_OVERRIDE:               "Ручной выбор цены не подходит (валюта, дата или единицы)",
  MISSING_INGREDIENT_PRICE_FOR_ORDER_CURRENCY: "Нет подходящей цены ингредиента в валюте заказа",
  MISSING_PACKAGING_PRICE_FOR_ORDER_CURRENCY:  "Нет подходящей цены упаковки в валюте заказа",
  OPEN_DRAFT_ALREADY_EXISTS_FOR_ORDER_ITEM:    "У этой позиции уже есть открытый черновик",
  FINALIZED_COSTING_CANNOT_BE_EDITED:          "Финализированная калькуляция не редактируется",
  FINALIZE_IN_PROGRESS:                        "Идёт финализация — попробуйте ещё раз",
  INVALID_RECIPE_YIELD:                        "Единица масштабирования не подходит рецепту",
  INVALID_SCALE_TARGET:                        "Укажите корректную единицу и значение для масштабирования (g/kg)",
};
export const mapCostingError = (err) => {
  const code = err?.response?.data?.detail?.code || err?.response?.data?.code || err?.response?.data?.detail;
  if (typeof code === "string" && COSTING_ERROR_MSG[code.toUpperCase()]) return COSTING_ERROR_MSG[code.toUpperCase()];
  return null;
};

export const COSTING_STATUS_LABELS = {
  draft: "Черновик", finalized: "Финализирован", superseded: "Заменён",
};
export const costingStatusTone = (s) => ({ draft: "draft", finalized: "ready", superseded: "planned" }[s] || "draft");

export const SCALING_METHODS = [
  { value: "quantity", label: "По количеству" },
  { value: "weight",   label: "По массе" },
  { value: "manual",   label: "Ручной коэффициент" },
];

// Backend enum: master_order/schemas.py::DeliveryType
export const DELIVERY_TYPES = [
  { value: "pickup",                 label: "Самовывоз" },
  { value: "confectioner_delivery",  label: "Доставка от кондитера" },
  { value: "courier",                label: "Курьер" },
  { value: "post_shipping",          label: "Почта / служба доставки" },
  { value: "intercity",              label: "Междугородняя" },
  { value: "custom",                 label: "Другое" },
];

// Backend enum: master_order/schemas.py::LifecycleStatus
export const LIFECYCLE_STATUSES = [
  { value: "draft",    label: "Черновик",  tone: "draft" },
  { value: "active",   label: "Активный",  tone: "work"  },
  { value: "archived", label: "В архиве",  tone: "draft" },
];

// Backend enum: master_order/schemas.py::BusinessStatus
// NOTE: "prepayment_received" is a business/workflow stage from the backend and
// stays inside the "Рабочий этап" filter — it is NOT a payment_status label.
// "calculation" is a workflow stage too — the Package 2 Costing frontend will
// have its own screen; this label here is only the business_status value name.
export const BUSINESS_STATUSES = [
  { value: "request",             label: "Запрос",         tone: "planned" },
  { value: "calculation",         label: "На калькуляции", tone: "draft"   },
  { value: "offer",               label: "Предложение",    tone: "draft"   },
  { value: "prepayment_received", label: "Внесена предоплата", tone: "work" },
  { value: "in_production",       label: "В работе",       tone: "work"    },
  { value: "ready",               label: "Готов",          tone: "ready"   },
  { value: "delivered",           label: "Выдан",          tone: "ready"   },
  { value: "cancelled",           label: "Отменён",        tone: "draft"   },
];

// Backend enum: master_order/schemas.py::PaymentStatus (read-only in OrderUpdate)
export const PAYMENT_STATUSES = [
  { value: "unpaid",    label: "Не оплачен", tone: "draft"   },
  { value: "partial",   label: "Частично",   tone: "planned" },
  { value: "paid_full", label: "Оплачен",    tone: "ready"   },
  { value: "refunded",  label: "Возврат",    tone: "draft"   },
];

export const businessLabel  = (v) => BUSINESS_STATUSES.find((s) => s.value === v)?.label  || v;
export const businessTone   = (v) => BUSINESS_STATUSES.find((s) => s.value === v)?.tone   || "draft";
export const lifecycleLabel = (v) => LIFECYCLE_STATUSES.find((s) => s.value === v)?.label || v;
export const lifecycleTone  = (v) => LIFECYCLE_STATUSES.find((s) => s.value === v)?.tone  || "draft";
export const paymentLabel   = (v) => PAYMENT_STATUSES.find((s) => s.value === v)?.label   || v;
export const paymentTone    = (v) => PAYMENT_STATUSES.find((s) => s.value === v)?.tone    || "draft";
export const deliveryLabel  = (v) => DELIVERY_TYPES.find((d) => d.value === v)?.label     || v;

// ISO 4217 subset — top-of-list common ones + extensible search field will accept
// any 3-letter ISO code (backend validator only checks 3 latin letters).
// ISO 4217 подборка — backend принимает ЛЮБОЙ валидный 3-буквенный ISO-код,
// это лишь удобная выборка для поиска. Названия — по-русски (по одному языку UI).
export const COMMON_CURRENCIES = [
  // Основные для СНГ/Европы
  { code: "EUR", label: "Евро" },
  { code: "USD", label: "Доллар США" },
  { code: "RUB", label: "Российский рубль" },
  { code: "BYN", label: "Белорусский рубль" },
  { code: "UAH", label: "Украинская гривна" },
  { code: "KZT", label: "Казахстанский тенге" },
  { code: "GEL", label: "Грузинский лари" },
  { code: "AMD", label: "Армянский драм" },
  { code: "AZN", label: "Азербайджанский манат" },
  { code: "MDL", label: "Молдавский лей" },
  { code: "UZS", label: "Узбекский сум" },
  { code: "KGS", label: "Киргизский сом" },
  { code: "TJS", label: "Таджикский сомони" },
  { code: "TMT", label: "Туркменский манат" },
  // Европа
  { code: "GBP", label: "Британский фунт" },
  { code: "CHF", label: "Швейцарский франк" },
  { code: "PLN", label: "Польский злотый" },
  { code: "CZK", label: "Чешская крона" },
  { code: "SEK", label: "Шведская крона" },
  { code: "NOK", label: "Норвежская крона" },
  { code: "DKK", label: "Датская крона" },
  { code: "ISK", label: "Исландская крона" },
  { code: "HUF", label: "Венгерский форинт" },
  { code: "RON", label: "Румынский лей" },
  { code: "BGN", label: "Болгарский лев" },
  { code: "RSD", label: "Сербский динар" },
  { code: "TRY", label: "Турецкая лира" },
  // Азия
  { code: "CNY", label: "Китайский юань" },
  { code: "JPY", label: "Японская иена" },
  { code: "KRW", label: "Южнокорейская вона" },
  { code: "HKD", label: "Гонконгский доллар" },
  { code: "SGD", label: "Сингапурский доллар" },
  { code: "THB", label: "Таиландский бат" },
  { code: "VND", label: "Вьетнамский донг" },
  { code: "IDR", label: "Индонезийская рупия" },
  { code: "MYR", label: "Малайзийский ринггит" },
  { code: "PHP", label: "Филиппинское песо" },
  { code: "INR", label: "Индийская рупия" },
  { code: "PKR", label: "Пакистанская рупия" },
  { code: "LKR", label: "Шри-ланкийская рупия" },
  // Ближний Восток
  { code: "AED", label: "Дирхам ОАЭ" },
  { code: "SAR", label: "Саудовский риял" },
  { code: "QAR", label: "Катарский риал" },
  { code: "KWD", label: "Кувейтский динар" },
  { code: "BHD", label: "Бахрейнский динар" },
  { code: "OMR", label: "Оманский риал" },
  { code: "JOD", label: "Иорданский динар" },
  { code: "ILS", label: "Израильский шекель" },
  { code: "EGP", label: "Египетский фунт" },
  // Америка / Океания / Африка
  { code: "CAD", label: "Канадский доллар" },
  { code: "MXN", label: "Мексиканское песо" },
  { code: "BRL", label: "Бразильский реал" },
  { code: "ARS", label: "Аргентинское песо" },
  { code: "CLP", label: "Чилийское песо" },
  { code: "COP", label: "Колумбийское песо" },
  { code: "AUD", label: "Австралийский доллар" },
  { code: "NZD", label: "Новозеландский доллар" },
  { code: "ZAR", label: "Южноафриканский ранд" },
];

/* --------------------------- Shopping List MVP ---------------------------- */
// Per-period recipe requirements + packaging + manual checklist — NOT a warehouse.
// Backend: /app/backend/master_order/services/shopping_list.py
export const getShoppingList = async ({ period_start, period_end }) =>
  (await api.get(p("/shopping-list"), { params: { period_start, period_end } })).data;

export const patchShoppingState = async ({ entity_type, entity_id, period_start, period_end, payload }) =>
  (await api.patch(p(`/shopping-state/${entity_type}/${entity_id}`), payload, {
    params: { period_start, period_end },
  })).data;

export const resetShoppingPurchased = async ({ period_start, period_end }) =>
  (await api.post(p("/shopping-state/reset-purchased"), null, {
    params: { period_start, period_end },
  })).data;

export const createManualPurchase = async ({ period_start, period_end, payload }) =>
  (await api.post(p("/shopping-manual"), payload, {
    params: { period_start, period_end },
  })).data;

export const deleteManualPurchase = async ({ entity_id, period_start, period_end }) =>
  (await api.delete(p(`/shopping-manual/${entity_id}`), {
    params: { period_start, period_end },
  })).data;


/* -------------------------- Production (Sprint P1) ------------------------ */
// Backend: /app/backend/master_order/production_router.py
export const PRODUCTION_STATUSES = [
  { value: "planned",     label: "Запланировано", tone: "warn"    },
  { value: "in_progress", label: "В работе",       tone: "work"    },
  { value: "done",        label: "Готово",         tone: "success" },
];
export const productionStatusLabel = (v) =>
  PRODUCTION_STATUSES.find((s) => s.value === v)?.label || v;
export const productionStatusTone = (v) =>
  PRODUCTION_STATUSES.find((s) => s.value === v)?.tone || "neutral";

export const listProductionTasks = async (params = {}) =>
  (await api.get(p("/production-tasks"), { params })).data;

export const getProductionTask = async (task_id) =>
  (await api.get(p(`/production-tasks/${task_id}`))).data;

export const createProductionTask = async (payload) =>
  (await api.post(p("/production-tasks"), payload)).data;

export const updateProductionTask = async (task_id, payload) =>
  (await api.patch(p(`/production-tasks/${task_id}`), payload)).data;

export const deleteProductionTask = async (task_id) =>
  (await api.delete(p(`/production-tasks/${task_id}`))).data;

export const reorderProductionTasks = async (entries) =>
  (await api.post(p("/production-tasks/reorder"), { entries })).data;


/* -------------------- Production Scenarios (Sprint P3) -------------------- */
// Backend: /app/backend/master_order/services/production_scenarios.py
export const listProductionScenarios = async (params = {}) =>
  (await api.get(p("/production-scenarios"), { params })).data;
export const getProductionScenario = async (id) =>
  (await api.get(p(`/production-scenarios/${id}`))).data;
export const createProductionScenario = async (payload) =>
  (await api.post(p("/production-scenarios"), payload)).data;
export const updateProductionScenario = async (id, payload) =>
  (await api.patch(p(`/production-scenarios/${id}`), payload)).data;
export const reorderProductionScenarios = async (entries) =>
  (await api.post(p("/production-scenarios/reorder"), { entries })).data;
export const applyProductionScenario = async (id, payload) =>
  (await api.post(p(`/production-scenarios/${id}/apply`), payload)).data;

/* -------- Production plan suggestion (UX correction sprint) ------- */
export const suggestProductionPlan = async ({ order_id, order_item_id = null }) =>
  (await api.get(p("/production-plan/suggest"),
    { params: { order_id, ...(order_item_id ? { order_item_id } : {}) } })).data;

export const listUnplannedOrders = async ({ date_from, date_to }) =>
  (await api.get(p("/production-plan/unplanned-orders"),
    { params: { date_from, date_to } })).data;


/* -------------------------- recipe cost (Sprint E-RecipeCost) -------------- */
export const resolveRecipeCost = async ({ items, currency_code = "EUR" }) =>
  (await api.post(p("/recipe-cost/resolve"), { items, currency_code })).data;

export const quickSavePrice = async (payload) =>
  (await api.post(p("/recipe-cost/quick-price"), payload)).data;

export const patchIngredient = async (id, payload) =>
  (await api.patch(p(`/ingredients/${id}`), payload)).data;


/* -------------------------- share link (Sprint Client-Link-3) -------------
 * Backend contract:
 *   POST /orders/{id}/share-link  → { id, order_id, raw_token, token_last4, created_at }
 *                                    (raw_token returned ONCE — server never stores it)
 *   GET  /orders/{id}/share-link  → { exists, active, id?, token_last4?, created_at? }
 *                                    (metadata only — raw_token unrecoverable)
 * POST is used for both create AND rotate: server atomically revokes the
 * previous active link and mints a new raw_token.
 * -------------------------------------------------------------------------- */
export const getOrderShareLinkStatus = async (orderId) =>
  (await api.get(p(`/orders/${orderId}/share-link`))).data;

export const createOrRotateOrderShareLink = async (orderId) =>
  (await api.post(p(`/orders/${orderId}/share-link`), {})).data;

export const revokeOrderShareLink = async (orderId) =>
  (await api.post(p(`/orders/${orderId}/share-link/revoke`), {})).data;
